export type HautImageSide = 'front' | 'left' | 'right' | string;
export type HautImageVariant = 'original' | 'anonymised' | 'aligned_face' | string;

export interface HautResultImage {
  side: HautImageSide;
  variant: HautImageVariant;
  label: string;
  url: string;
  description: string;
}

export interface HautMetricOverlayImage extends HautResultImage {
  metricKey: string;
  metricLabel: string;
}

export interface HautMetricPresentation {
  key: string;
  label: string;
  score?: number;
  grade?: number;
  gradeDescription?: string;
  tag?: string;
  valueBadges: string[];
  maskImages: HautMetricOverlayImage[];
  emptyMaskReason?: string;
}

export interface HautInsightItem {
  label: string;
  value: string;
}

export interface HautScoreSignal {
  key: string;
  label: string;
  score: number;
  tag?: string;
  grade?: number;
}

export interface HautFaceSkinPresentation {
  allAlgorithmsCalculated?: boolean;
  detectionErrors: Array<{ side: string; value: string }>;
  predictedImages: HautResultImage[];
  metrics: HautMetricPresentation[];
  snapshot: HautInsightItem[];
  focusAreas: HautScoreSignal[];
  stableSignals: HautScoreSignal[];
}

export interface HautResultPresentation {
  faceSkin?: HautFaceSkinPresentation;
}

const IMAGE_VARIANT_LABELS: Record<string, string> = {
  original: 'Original',
  anonymised: 'Anonymised',
  aligned_face: 'Aligned face',
};

const IMAGE_VARIANT_DESCRIPTIONS: Record<string, string> = {
  original: 'Original uploaded view returned by the analysis service.',
  anonymised: 'Privacy-safe face view returned when available.',
  aligned_face: 'Face-normalized view used for consistent visual comparison.',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const isLikelyImageUrl = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^https?:\/\//i.test(value) &&
  (/\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(value) || value.includes('storage.googleapis.com'));

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const toDisplayString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return undefined;
};

export const describeGrade = (grade: number): string => {
  if (grade <= 1) return 'Grade 1 of 5, best condition';
  if (grade >= 4) return `Grade ${grade} of 5, worse condition`;
  if (grade === 3) return 'Grade 3 of 5, average condition';
  return `Grade ${grade} of 5, good condition`;
};

const humanizeKey = (key: string): string =>
  key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const variantLabel = (variant: string): string => IMAGE_VARIANT_LABELS[variant] ?? humanizeKey(variant);

const variantDescription = (variant: string): string =>
  IMAGE_VARIANT_DESCRIPTIONS[variant] ?? 'Result image returned by the analysis service.';

const findFaceSkinResult = (raw: unknown): Record<string, unknown> | null => {
  if (!isRecord(raw)) return null;

  const haut = raw['haut'];
  const hautRecord = isRecord(haut) ? haut : undefined;
  const hautSkin = hautRecord && isRecord(hautRecord['skin']) ? hautRecord['skin'] : undefined;
  const skin = isRecord(raw['skin']) ? raw['skin'] : undefined;
  const candidates: unknown[] = [
    raw['face_skin_analysis_3'],
    raw['face_skin_metrics_3'],
    hautRecord?.['face_skin_analysis_3'],
    hautRecord?.['face_skin_metrics_3'],
    hautSkin?.['face_skin_analysis_3'],
    hautSkin?.['face_skin_metrics_3'],
    skin?.['face_skin_analysis_3'],
    skin?.['face_skin_metrics_3'],
  ];

  const direct = candidates.find(isRecord);
  if (direct) return direct;

  if ('parameters' in raw || 'predicted_images' in raw) return raw;
  return null;
};

const collectPredictedImages = (faceSkin: Record<string, unknown>): HautResultImage[] => {
  const predictedImages = faceSkin['predicted_images'];
  if (!isRecord(predictedImages)) return [];

  return Object.entries(predictedImages).flatMap(([side, sideImages]) => {
    if (!isRecord(sideImages)) return [];
    return Object.entries(sideImages).flatMap(([variant, url]) => {
      if (variant !== 'aligned_face') return [];
      if (!isLikelyImageUrl(url)) return [];
      return [
        {
          side,
          variant,
          label: `${humanizeKey(side)} ${variantLabel(variant)}`,
          url,
          description: variantDescription(variant),
        },
      ];
    });
  });
};

const collectMaskImages = (
  metricKey: string,
  metricLabel: string,
  metricValue: Record<string, unknown>,
): HautMetricOverlayImage[] => {
  const masks = metricValue['masks'];
  if (!isRecord(masks)) return [];

  return Object.entries(masks).flatMap(([side, sideMasks]) => {
    if (!isRecord(sideMasks)) return [];
    return Object.entries(sideMasks).flatMap(([variant, url]) => {
      if (variant !== 'aligned_face') return [];
      if (!isLikelyImageUrl(url)) return [];
      return [
        {
          metricKey,
          metricLabel,
          side,
          variant,
          label: `${metricLabel} ${humanizeKey(side)} ${variantLabel(variant)} overlay`,
          url,
          description: 'Mask overlay returned for this metric and image variant.',
        },
      ];
    });
  });
};

const buildValueBadges = (metricValue: Record<string, unknown>): string[] => {
  const badges: string[] = [];
  const tag = toDisplayString(metricValue['tag']);
  const classification = toDisplayString(metricValue['classification']);
  const amount = toDisplayString(metricValue['amount']);
  const density = toDisplayString(metricValue['density']);
  const age = toDisplayString(metricValue['age'] ?? metricValue['eyes_age']);
  const ita = toDisplayString(metricValue['ita']);

  if (tag) badges.push(`Tag: ${tag}`);
  if (ita) badges.push(`ITA: ${ita}`);
  if (classification) badges.push(`Classification: ${classification}`);
  if (amount) badges.push(`Amount: ${amount}`);
  if (density) badges.push(`Density: ${density}`);
  if (age) badges.push(`Age: ${age}`);
  if (isRecord(metricValue['areas'])) badges.push(`Areas: ${Object.keys(metricValue['areas']).length}`);

  return badges;
};

const buildMetricPresentation = ([key, value]: [string, unknown]): HautMetricPresentation | null => {
  if (!isRecord(value)) return null;

  const label = humanizeKey(key);
  const score = toNumber(value['score']);
  const grade = toNumber(value['grade']);
  const tag = toDisplayString(value['tag']);
  const maskImages = collectMaskImages(key, label, value);
  const hasMaskContainer = isRecord(value['masks']);

  return {
    key,
    label,
    ...(score !== undefined ? { score } : {}),
    ...(grade !== undefined ? { grade, gradeDescription: describeGrade(grade) } : {}),
    ...(tag ? { tag } : {}),
    valueBadges: buildValueBadges(value),
    maskImages,
    ...(hasMaskContainer && maskImages.length === 0
      ? { emptyMaskReason: 'No overlay image returned for this score/result.' }
      : {}),
  };
};

const collectMetrics = (faceSkin: Record<string, unknown>): HautMetricPresentation[] => {
  const parameters = faceSkin['parameters'];
  if (!isRecord(parameters)) return [];

  return Object.entries(parameters)
    .map(buildMetricPresentation)
    .filter((item): item is HautMetricPresentation => Boolean(item));
};

const collectDetectionErrors = (faceSkin: Record<string, unknown>): Array<{ side: string; value: string }> => {
  const errors = faceSkin['detection_errors'];
  if (!isRecord(errors)) return [];

  return Object.entries(errors)
    .map(([side, value]) => ({ side, value: toDisplayString(value) ?? 'None' }))
    .filter((item) => item.value !== 'None');
};

const metricByKey = (metrics: HautMetricPresentation[], key: string): HautMetricPresentation | undefined =>
  metrics.find((metric) => metric.key === key);

const firstBadgeValue = (metric: HautMetricPresentation | undefined, prefixes: string[]): string | undefined => {
  if (!metric) return undefined;
  for (const prefix of prefixes) {
    const badge = metric.valueBadges.find((value) => value.startsWith(prefix));
    if (badge) return badge.slice(prefix.length).trim();
  }
  return undefined;
};

const buildSnapshot = (metrics: HautMetricPresentation[]): HautInsightItem[] => {
  const skinType = firstBadgeValue(metricByKey(metrics, 'skin_type'), ['Classification:', 'Tag:']);
  const skinTone = firstBadgeValue(metricByKey(metrics, 'skintone'), ['Classification:', 'ITA:', 'Tag:']);
  const perceivedAge = firstBadgeValue(metricByKey(metrics, 'age'), ['Age:']);
  const imageQuality = metricByKey(metrics, 'quality')?.score;

  return [
    ...(skinType ? [{ label: 'Skin type', value: skinType }] : []),
    ...(skinTone ? [{ label: 'Skin tone', value: skinTone }] : []),
    ...(perceivedAge ? [{ label: 'Perceived age', value: perceivedAge }] : []),
    ...(imageQuality !== undefined ? [{ label: 'Image quality', value: String(imageQuality) }] : []),
  ];
};

const toScoreSignal = (metric: HautMetricPresentation): HautScoreSignal | null => {
  if (metric.score === undefined) return null;
  return {
    key: metric.key,
    label: metric.label,
    score: metric.score,
    ...(metric.tag ? { tag: metric.tag } : {}),
    ...(metric.grade !== undefined ? { grade: metric.grade } : {}),
  };
};

const collectScoreSignals = (metrics: HautMetricPresentation[]): HautScoreSignal[] =>
  metrics.map(toScoreSignal).filter((item): item is HautScoreSignal => Boolean(item));

const buildFocusAreas = (metrics: HautMetricPresentation[]): HautScoreSignal[] =>
  collectScoreSignals(metrics)
    .filter((signal) => signal.key !== 'quality')
    .sort((left, right) => left.score - right.score)
    .slice(0, 3);

const buildStableSignals = (metrics: HautMetricPresentation[]): HautScoreSignal[] =>
  collectScoreSignals(metrics)
    .sort((left, right) => right.score - left.score)
    .slice(0, 3);

export const getPreferredOverlayBaseImage = (
  predictedImages: HautResultImage[],
  maskImage: HautMetricOverlayImage,
): HautResultImage | undefined =>
  predictedImages.find((image) => image.side === maskImage.side && image.variant === maskImage.variant) ??
  predictedImages.find((image) => image.side === maskImage.side && image.variant === 'aligned_face') ??
  predictedImages.find((image) => image.side === maskImage.side && image.variant === 'original') ??
  predictedImages.find((image) => image.side === 'front' && image.variant === 'aligned_face') ??
  predictedImages.find((image) => image.side === 'front' && image.variant === 'original');

export const buildHautResultPresentation = (raw: unknown): HautResultPresentation => {
  const faceSkin = findFaceSkinResult(raw);
  if (!faceSkin) return {};
  const metrics = collectMetrics(faceSkin);

  return {
    faceSkin: {
      ...(typeof faceSkin['all_algorithms_calculated'] === 'boolean'
        ? { allAlgorithmsCalculated: faceSkin['all_algorithms_calculated'] }
        : {}),
      detectionErrors: collectDetectionErrors(faceSkin),
      predictedImages: collectPredictedImages(faceSkin),
      metrics,
      snapshot: buildSnapshot(metrics),
      focusAreas: buildFocusAreas(metrics),
      stableSignals: buildStableSignals(metrics),
    },
  };
};
